import React, { useState } from "react";
import { motion } from "framer-motion";
import { startCheckout } from "../lib/checkout";
import { TIERS, getAccentVariant } from "../data/tiers";
import { TechBackground } from "./TechBackground";
import { TAP } from "../lib/motion";
import { useAvailability } from "../hooks/useAvailability";

const COMPACT = [
  { id: "network", name: "NETWORK", price: "99/MO", value: "$99 CREDIT + 20% OFF", accent: "cyan" },
  { id: "pro", name: "PRO", price: "199/MO", value: "$250 CREDIT + 30% OFF", accent: "pro", tag: "MOST POPULAR" },
  { id: "elite", name: "ELITE", price: "299/MO", value: "$400 CREDIT + 40% OFF", accent: "gold" },
];

const cardBorders = {
  pro: { border: "1px solid rgba(0,212,255,0.5)" },
  gold: { border: "1px solid rgba(212,175,55,0.3)" },
  default: {},
};
const valueColors = { pro: "text-cyan", gold: "text-gold", default: "text-dim" };
const ctaStyles = { pro: "cta-primary", gold: "cta-gold", default: "cta-outline" };

const selectLabel = (soldOut, loading) => {
  if (soldOut) return "SOLD OUT";
  return loading ? "REDIRECTING…" : "SELECT";
};

const headingAnimation = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8 },
};
const subAnimation = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true },
  transition: { duration: 0.8, delay: 0.1 },
};

const CompactTierButton = ({ tier, index, loading, onSelect, availability }) => {
  const variant = getAccentVariant(tier.accent);
  const isPro = variant === "pro";
  const isGold = variant === "gold";
  const soldOut = availability?.sold_out;

  return (
    <motion.button
      onClick={() => onSelect(tier.id)}
      disabled={loading || soldOut}
      whileTap={TAP}
      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className={`relative text-left glass p-6 flex flex-col disabled:opacity-60 ${isPro ? "glow-cyan sm:scale-[1.05] sm:-my-1 order-first sm:order-none" : ""}`}
      style={cardBorders[variant]}
      data-testid={`final-cta-${tier.id}`}
    >
      {tier.tag && (
        <span className="eyebrow text-[0.55rem] self-start px-2.5 py-1 mb-3 bg-gradient-to-r from-[#00D4FF] to-[#00E676] text-[#05070a] font-600">{tier.tag}</span>
      )}
      <span className={`font-display text-lg font-700 tracking-wide ${isGold ? "text-gold" : "text-white"}`}>{tier.name}</span>
      <span className="font-display text-2xl font-700 text-white mt-1">${tier.price}</span>
      <span className={`mt-3 text-xs font-display tracking-wide ${valueColors[variant]}`}>{tier.value}</span>
      <span className={`mt-5 inline-flex justify-center py-2.5 text-xs ${ctaStyles[variant]}`}>
        {selectLabel(soldOut, loading)}
      </span>
    </motion.button>
  );
};

export const FinalCTA = () => {
  const [loading, setLoading] = useState(null);
  const availability = useAvailability();
  const go = (id) => startCheckout(TIERS[id].lookupKey, (v) => setLoading(v ? id : null));

  return (
    <section className="relative py-24 sm:py-32 grain overflow-hidden" data-testid="final-cta">
      <TechBackground variant="hero" />
      <div className="relative z-10 max-w-5xl mx-auto px-5 text-center">
        <motion.h2 {...headingAnimation} className="font-display text-4xl sm:text-6xl lg:text-7xl font-700 tracking-tighter text-white leading-[0.95]">
          ENTER THE NETWORK.
        </motion.h2>
        <motion.p {...subAnimation} className="mt-6 text-dim font-light text-base sm:text-lg">
          Choose your level. Unlock your benefits. Start optimizing.
        </motion.p>

        <div className="mt-14 grid sm:grid-cols-3 gap-4 items-stretch max-w-3xl mx-auto">
          {COMPACT.map((c, i) => (
            <CompactTierButton key={c.id} tier={c} index={i} loading={loading === c.id} onSelect={go} availability={availability?.[TIERS[c.id].lookupKey]} />
          ))}
        </div>
      </div>
    </section>
  );
};
